import { memo, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import DetailDrawer from '@/components/DetailDrawer/DetailDrawer';
import GuidancePanel, { GUIDANCE } from '@/components/GuidancePanel';
import { createFetchAttestationsApi } from '@/screens/Attestations/api.fetch';
import type { AttestationRow } from '@/screens/Attestations/api';

import type {
  DashboardApi,
  DashboardKpis,
  DashboardTimeWindow,
  RecentRun,
} from './api';
import { createFetchDashboardApi } from './api.fetch';
import PitchDeckModal from './PitchDeckModal';
import './Dashboard.css';

interface DashboardProps {
  /**
   * Injected API client. Defaults to the mock so dev/test never touches the
   * network. The real client will come from an OpenAPI-generated module
   * per ADR-0013.
   */
  api?: DashboardApi;
  /**
   * Injected Attestations list fetcher. The Dashboard needs the attestation
   * queue summary; we accept an injected fetcher so tests can keep the
   * mock-API factory in one place.
   */
  listAttestations?: () => Promise<AttestationRow[]>;
}

const DEFAULT_API: DashboardApi = createFetchDashboardApi();
const DEFAULT_ATTESTATIONS = createFetchAttestationsApi();
const DEFAULT_LIST_ATTESTATIONS: () => Promise<AttestationRow[]> = () =>
  DEFAULT_ATTESTATIONS.listAttestations({});

const TIME_WINDOW_OPTIONS: Array<{ value: DashboardTimeWindow; label: string }> = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

export default function Dashboard({
  api = DEFAULT_API,
  listAttestations = DEFAULT_LIST_ATTESTATIONS,
}: DashboardProps) {
  const queryClient = useQueryClient();
  const [timeWindow, setTimeWindow] = useState<DashboardTimeWindow>('24h');
  const [selectedRun, setSelectedRun] = useState<RecentRun | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const [pitchDeckOpen, setPitchDeckOpen] = useState<boolean>(false);
  const pitchDeckTriggerRef = useRef<HTMLButtonElement | null>(null);

  // `staleTime: 60_000` on the list queries — the Dashboard is dense and
  // revisited often; the one-minute cache eliminates a cascade of 4-6
  // serial refetches per tab return without harming freshness (a manual
  // Refresh still invalidates).
  const recentRunsQ = useQuery({
    queryKey: ['dashboard', 'recent-runs', { window: timeWindow }],
    queryFn: () => api.getRecentRuns({ window: timeWindow }),
    staleTime: 60_000,
  });
  const driftFlagsQ = useQuery({
    queryKey: ['dashboard', 'drift-flags'],
    queryFn: () => api.getDriftFlags(),
    staleTime: 60_000,
  });
  const approvalsQ = useQuery({
    queryKey: ['dashboard', 'evidence-approvals'],
    queryFn: () => api.getPendingEvidenceApprovals(),
    staleTime: 60_000,
  });
  const kpisQ = useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: () => api.getKpis(),
    staleTime: 60_000,
  });
  const attestationsQ = useQuery({
    queryKey: ['dashboard', 'attestations'],
    queryFn: () => listAttestations(),
    staleTime: 60_000,
  });

  const driftFlagCount = driftFlagsQ.data?.length ?? 0;
  const pendingApprovals = useMemo(
    () => (approvalsQ.data ?? []).filter((a) => a.state === 'AWAITING SIG').length,
    [approvalsQ.data],
  );

  function handleRefresh() {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    setLastRefreshed(new Date().toISOString());
  }

  return (
    <div>
      <PageHead
        pendingApprovals={pendingApprovals}
        driftFlagCount={driftFlagCount}
        onOpenPitchDeck={() => setPitchDeckOpen(true)}
        pitchDeckTriggerRef={pitchDeckTriggerRef}
      />

      <PitchDeckModal
        open={pitchDeckOpen}
        onClose={() => setPitchDeckOpen(false)}
        returnFocusTo={pitchDeckTriggerRef}
      />

      <GuidancePanel screenKey="dashboard" {...GUIDANCE.dashboard} />

      <DashboardControls
        window={timeWindow}
        onWindowChange={setTimeWindow}
        onRefresh={handleRefresh}
        refreshing={recentRunsQ.isFetching}
        lastRefreshed={lastRefreshed}
      />

      <QuickActions />

      <KpiStrip kpis={kpisQ.data} loading={kpisQ.isLoading} />

      <div className="grid-2">
        <ActiveReplayCard
          runs={recentRunsQ.data}
          loading={recentRunsQ.isLoading}
        />
        <AttestationQueueCard
          rows={attestationsQ.data}
          loading={attestationsQ.isLoading}
        />
      </div>

      <RecentRunsCard
        runs={recentRunsQ.data}
        loading={recentRunsQ.isLoading}
        error={recentRunsQ.error}
        onRowClick={setSelectedRun}
      />

      {selectedRun && (
        <DetailDrawer
          testId="dashboard-run-drawer"
          title={`Replay ${selectedRun.receipt}`}
          subtitle={`${selectedRun.scenario} · ${selectedRun.window}`}
          payload={selectedRun}
          onClose={() => setSelectedRun(null)}
        />
      )}
    </div>
  );
}

/* ---------- Dashboard controls ---------- */

interface DashboardControlsProps {
  window: DashboardTimeWindow;
  onWindowChange: (next: DashboardTimeWindow) => void;
  onRefresh: () => void;
  refreshing: boolean;
  lastRefreshed: string | null;
}

function DashboardControls({
  window,
  onWindowChange,
  onRefresh,
  refreshing,
  lastRefreshed,
}: DashboardControlsProps) {
  return (
    <div className="dashboard-controls" data-testid="dashboard-controls">
      <label className="dashboard-controls__window">
        <span className="eyebrow">TIME WINDOW</span>
        <select
          aria-label="Select dashboard time window"
          value={window}
          onChange={(e) => onWindowChange(e.target.value as DashboardTimeWindow)}
          data-testid="dashboard-window-select"
        >
          {TIME_WINDOW_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <div className="dashboard-controls__actions">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onRefresh}
          data-testid="dashboard-refresh"
          aria-busy={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        <a
          className="btn btn-ghost"
          href="/replays"
          data-testid="dashboard-view-all-runs"
        >
          View all runs →
        </a>
        {lastRefreshed && (
          <span
            className="dashboard-controls__status mono"
            role="status"
            aria-live="polite"
            data-testid="dashboard-refreshed-at"
          >
            Refreshed {formatRefreshedAt(lastRefreshed)}
          </span>
        )}
      </div>
    </div>
  );
}

function formatRefreshedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = d.getUTCHours().toString().padStart(2, '0');
  const mm = d.getUTCMinutes().toString().padStart(2, '0');
  const ss = d.getUTCSeconds().toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}Z`;
}

/* ---------- Page head ---------- */

function PageHead({
  pendingApprovals,
  driftFlagCount,
  onOpenPitchDeck,
  pitchDeckTriggerRef,
}: {
  pendingApprovals: number;
  driftFlagCount: number;
  onOpenPitchDeck: () => void;
  pitchDeckTriggerRef: React.RefObject<HTMLButtonElement>;
}) {
  return (
    <div className="page-head">
      <div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="eyebrow">§ OVERVIEW</span>
          <span className="chip">
            <span className="dot" />
            ALL LANES GREEN
          </span>
          {pendingApprovals > 0 && (
            <span className="chip warn">
              <span className="dot" />
              {pendingApprovals} ATTESTATION PENDING
            </span>
          )}
          {driftFlagCount > 0 && (
            <span
              className="chip warn"
              data-testid="drift-flags-badge"
              aria-label={`${driftFlagCount} drift flags`}
            >
              <span className="dot" />
              {driftFlagCount} DRIFT FLAG{driftFlagCount === 1 ? '' : 'S'}
            </span>
          )}
        </div>
        <h1>Good afternoon, Elena.</h1>
        <p
          style={{ fontSize: 14, color: 'var(--slate-500)', margin: '8px 0 0', maxWidth: 680 }}
          data-testid="dashboard-intro"
        >
          Your validation overview — replay parity, evidence quorum, and drift signals for the
          selected window, computed from the same deterministic pipeline that feeds the regulator
          packages. Refreshes pull the latest cached projection; every number here ties back to a
          sealable replay receipt.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <a className="btn btn-ghost" href="/audit-packages">
          Export audit
        </a>
        <button
          ref={pitchDeckTriggerRef}
          type="button"
          className="btn btn-ghost dashboard-deck-trigger dashboard-deck-trigger--labelled"
          title="Open the Project Sentinel pitch deck (25 slides, fullscreen available)"
          data-testid="dashboard-pitch-deck-trigger"
          onClick={onOpenPitchDeck}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <rect x="2.5" y="3" width="11" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <path d="M4.5 12.5h7M5.5 14h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <span>Pitch deck</span>
        </button>
        <a className="btn btn-primary" href="/replays">
          New replay →
        </a>
      </div>
    </div>
  );
}

/* ---------- Quick actions ---------- */

interface QuickAction {
  key: string;
  label: string;
  href: string;
  hint: string;
  testId: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    key: 'replay',
    label: 'Start replay',
    href: '/replays',
    hint: 'Kick off a deterministic replay of a published scenario version.',
    testId: 'dashboard-quick-replay',
  },
  {
    key: 'divergence',
    label: 'Review divergence queue',
    href: '/divergence',
    hint: 'Matched / Missed / Extra / Threshold-drift rows awaiting analyst triage.',
    testId: 'dashboard-quick-divergence',
  },
  {
    key: 'seal',
    label: 'Seal audit package',
    href: '/audit-packages',
    hint: 'Roll a run into a 3-of-5 signed, regulator-ready evidence bundle.',
    testId: 'dashboard-quick-seal',
  },
];

function QuickActions() {
  return (
    <section
      className="dashboard-quick-actions"
      aria-label="Quick actions"
      data-testid="dashboard-quick-actions"
    >
      <div className="eyebrow">QUICK ACTIONS</div>
      <div className="dashboard-quick-actions__row">
        {QUICK_ACTIONS.map((a) => (
          <a
            key={a.key}
            className="dashboard-quick-action"
            href={a.href}
            title={a.hint}
            data-testid={a.testId}
          >
            <span className="dashboard-quick-action__label">{a.label}</span>
            <span className="dashboard-quick-action__hint">{a.hint}</span>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ---------- KPI strip ---------- */

interface KpiStripProps {
  kpis: DashboardKpis | undefined;
  loading: boolean;
}

function KpiStrip({ kpis, loading }: KpiStripProps) {
  const cells: Array<{ label: string; value: string; suffix?: string }> = [
    {
      label: 'PARITY · 30D',
      value: kpis?.parity30d ?? (loading ? '—' : '—'),
    },
    {
      label: 'REPLAYS · 30D',
      value:
        kpis && typeof kpis.replays30d === 'number'
          ? kpis.replays30d.toLocaleString()
          : loading
            ? '—'
            : '—',
    },
    {
      label: 'DIVERGENCES OPEN',
      value:
        kpis && typeof kpis.divergencesOpen === 'number'
          ? kpis.divergencesOpen.toString()
          : loading
            ? '—'
            : '—',
    },
    {
      label: 'SEALED PACKAGES',
      value:
        kpis && typeof kpis.sealedPackages === 'number'
          ? kpis.sealedPackages.toString()
          : loading
            ? '—'
            : '—',
    },
    {
      label: 'EVENTS · 24H',
      value: kpis?.events24h ?? (loading ? '—' : '—'),
    },
  ];
  return (
    <div className="grid-kpi" data-testid="dashboard-kpis">
      {cells.map((k, idx) => (
        <div
          key={k.label}
          className={`kpi-cell${idx < cells.length - 1 ? ' kpi-cell--divider' : ''}`}
        >
          <div className="eyebrow">{k.label}</div>
          <div className="serif kpi-value">
            {k.value}
            {k.suffix && <span className="kpi-suffix">{k.suffix}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Active replay card ---------- */

interface ActiveReplayCardProps {
  runs: RecentRun[] | undefined;
  loading: boolean;
}

/**
 * Picks the most interesting "active" run off the recent-runs feed:
 * prefer the single `RUNNING` row; fall back to `AWAITING SIG` (the next
 * step in the pipeline) so the card is never empty on a healthy deck.
 * Returns `null` only when nothing is in-flight.
 */
function pickActiveRun(runs: RecentRun[] | undefined): RecentRun | null {
  if (!runs || runs.length === 0) return null;
  const running = runs.find((r) => r.status === 'RUNNING');
  if (running) return running;
  const awaiting = runs.find((r) => r.status === 'AWAITING SIG');
  return awaiting ?? null;
}

/**
 * Best-effort progress estimate for the animated bar. Without a server-side
 * progress stream we use the run's elapsed millis (0 when in-flight) mod 10s
 * so the bar still animates; for `AWAITING SIG` we pin it at 100 %.
 */
function estimateProgress(run: RecentRun): number {
  if (run.status === 'AWAITING SIG' || run.status === 'SEALED') return 1;
  if (run.durationMs <= 0) return 0.45;
  const cap = 10_000;
  return Math.max(0.05, Math.min(0.95, (run.durationMs % cap) / cap));
}

const ActiveReplayCard = memo(function ActiveReplayCard({
  runs,
  loading,
}: ActiveReplayCardProps) {
  const active = pickActiveRun(runs);
  if (loading) {
    return (
      <section className="card">
        <header className="card-head">
          <div className="eyebrow">ACTIVE REPLAY · LIVE</div>
        </header>
        <div role="status" aria-live="polite" style={{ padding: 22, fontSize: 13 }}>
          Loading active replay…
        </div>
      </section>
    );
  }
  if (!active) {
    return (
      <section className="card" data-testid="active-replay-empty">
        <header className="card-head">
          <div className="eyebrow">ACTIVE REPLAY · LIVE</div>
          <span className="chip">
            <span className="dot" />
            IDLE
          </span>
        </header>
        <div style={{ padding: 22 }}>
          <p style={{ fontSize: 13, color: 'var(--slate-500)', margin: 0 }}>
            No replay currently executing — click <strong>New replay</strong> to start one.
          </p>
          <a
            className="btn btn-primary"
            href="/replays"
            style={{ marginTop: 14, display: 'inline-block' }}
            data-testid="active-replay-cta"
          >
            Go to replays →
          </a>
        </div>
      </section>
    );
  }
  const progress = estimateProgress(active);
  const pct = Math.round(progress * 100);
  const elapsedLabel =
    active.durationMs > 0
      ? formatDuration(active.durationMs)
      : active.status === 'RUNNING'
        ? 'in-flight'
        : 'awaiting quorum';
  return (
    <section className="card" data-testid="active-replay-live">
      <header className="card-head">
        <div className="eyebrow">ACTIVE REPLAY · LIVE</div>
        <span className={statusChipClass(active.status)}>
          <span className="dot" />
          {active.status} {elapsedLabel}
        </span>
      </header>
      <div style={{ padding: 22 }}>
        <div className="mono" style={{ fontSize: 14, color: 'var(--ink)', marginBottom: 18 }}>
          {active.receipt} · {active.scenario}
        </div>
        <div className="active-replay-matrix">
          <div className="active-replay-matrix__labels">
            <div>Quantexa</div>
            <div>Actimize</div>
            <div>SAS AML</div>
            <div className="active-replay-matrix__sentinel">Sentinel</div>
          </div>
          <div className="active-replay-matrix__tracks">
            <div className="track">
              <span
                className="bar bar-navy-faint"
                style={{ left: '4%', width: `${Math.max(4, pct - 8)}%` }}
              />
            </div>
            <div className="track">
              <span
                className="bar bar-slate"
                style={{ left: '8%', width: `${Math.max(4, pct - 16)}%` }}
              />
              <span
                className="bar bar-warn"
                style={{ left: `${pct}%`, width: `${Math.max(4, Math.min(18, 100 - pct))}%` }}
              />
            </div>
            <div className="track">
              <span
                className="bar bar-slate"
                style={{ left: '6%', width: `${Math.max(10, pct + 14)}%` }}
              />
            </div>
            <div className="track track--sentinel">
              <span
                className="bar bar-navy"
                style={{ left: '4%', width: `${Math.max(4, pct - 4)}%` }}
              />
            </div>
            <div className="playhead" style={{ left: `${pct}%` }} />
          </div>
        </div>
        <div
          className="mono"
          style={{
            marginTop: 16,
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11.5,
            color: 'var(--slate-500)',
          }}
        >
          <span>
            {pct}% · window {active.window}
          </span>
          <span>PARITY {active.parity}</span>
        </div>
      </div>
    </section>
  );
});

/* ---------- Attestation queue card ---------- */

interface AttestationQueueCardProps {
  rows: AttestationRow[] | undefined;
  loading: boolean;
}

/**
 * Chip label for a single attestation. PARTIAL rows render as
 * `AWAITING SIG m/n` so analysts can tell at a glance how far off quorum
 * is. Sealed rows carry a relative time stamp.
 */
function attestationChip(row: AttestationRow): { cls: string; label: string } {
  if (row.status === 'PARTIAL') {
    return { cls: 'chip warn', label: `AWAITING SIG ${row.collected}/${row.quorum}` };
  }
  if (row.status === 'PENDING') {
    return { cls: 'chip warn', label: `PENDING ${row.collected}/${row.quorum}` };
  }
  if (row.status === 'EXPIRED') {
    return { cls: 'chip fail', label: 'EXPIRED' };
  }
  const sealed = row.sealedAt ? ` ${relativeMinutes(row.sealedAt)}` : '';
  return { cls: 'chip', label: `SEALED${sealed}` };
}

function relativeMinutes(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < 60_000) return 'just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
}

const AttestationQueueCard = memo(function AttestationQueueCard({
  rows,
  loading,
}: AttestationQueueCardProps) {
  const top = (rows ?? []).slice(0, 3);
  return (
    <section className="card" data-testid="attestation-queue">
      <header className="card-head">
        <div className="eyebrow">ATTESTATION QUEUE</div>
        <a
          href="/attestations"
          style={{ fontSize: 11.5, color: 'var(--navy)', fontWeight: 500 }}
        >
          View all →
        </a>
      </header>
      {loading && top.length === 0 && (
        <div role="status" aria-live="polite" style={{ padding: 20, fontSize: 13 }}>
          Loading attestation queue…
        </div>
      )}
      {!loading && top.length === 0 && (
        <div
          style={{ padding: 22, fontSize: 13, color: 'var(--slate-500)' }}
          data-testid="attestation-queue-empty"
        >
          No attestations in flight — every run has been sealed.
        </div>
      )}
      {top.map((row) => {
        const chip = attestationChip(row);
        const needsSig = row.status === 'PARTIAL' || row.status === 'PENDING';
        return (
          <div
            key={row.attestationId as unknown as string}
            className="attestation-row"
            data-testid={`attestation-row-${row.receipt}`}
          >
            <div className="attestation-row__meta">
              <div className="mono" style={{ fontSize: 12 }}>
                {row.receipt}
              </div>
              <span className={chip.cls}>
                <span className="dot" />
                {chip.label}
              </span>
            </div>
            <div style={{ fontSize: 13, marginBottom: needsSig ? 10 : 0 }}>{row.scope}</div>
            {needsSig && (
              <a className="btn btn-primary attestation-row__cta" href="/attestations">
                Sign &amp; seal now
              </a>
            )}
          </div>
        );
      })}
    </section>
  );
});

/* ---------- Recent runs card ---------- */

interface RecentRunsCardProps {
  runs: RecentRun[] | undefined;
  loading: boolean;
  error: unknown;
  onRowClick: (run: RecentRun) => void;
}

function RecentRunsCard({ runs, loading, error, onRowClick }: RecentRunsCardProps) {
  const hasRuns = !!runs && runs.length > 0;

  return (
    <section className="card">
      <header className="card-head">
        <h2 className="eyebrow" id="recent-runs-heading" style={{ margin: 0 }}>
          RECENT REPLAY SESSIONS
        </h2>
        <a href="/replays" style={{ fontSize: 11.5, color: 'var(--navy)', fontWeight: 500 }}>
          Open all →
        </a>
      </header>
      <div className="table-wrap">
        {error ? (
          <div
            role="alert"
            data-testid="recent-runs-error"
            style={{ padding: 20, fontSize: 13, color: 'var(--fail)' }}
          >
            Couldn&apos;t load recent runs. {errorMessage(error)}
          </div>
        ) : null}
        {!error && loading && (
          <div role="status" aria-live="polite" style={{ padding: 20, fontSize: 13 }}>
            Loading recent runs…
          </div>
        )}
        {!error && !loading && !hasRuns && (
          <div
            data-testid="recent-runs-empty"
            style={{ padding: 28, textAlign: 'center', color: 'var(--slate-500)', fontSize: 13 }}
          >
            No replay sessions in the selected window.
          </div>
        )}
        {!error && !loading && hasRuns && (
          <table className="sentinel-table" aria-labelledby="recent-runs-heading">
            <caption className="visually-hidden">Recent replay sessions</caption>
            <thead>
              <tr>
                <th>Receipt</th>
                <th>Scenario</th>
                <th>Window</th>
                <th>Parity</th>
                <th>Duration</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {runs!.map((run) => (
                <tr
                  key={run.receipt}
                  className="data-table-row-clickable"
                  onClick={() => onRowClick(run)}
                  data-testid={`recent-runs-row-${run.receipt}`}
                >
                  <td className="mono">{run.receipt}</td>
                  <td>
                    <strong>{run.scenario}</strong>
                  </td>
                  <td className="mono">{run.window}</td>
                  <td className="mono">{run.parity}</td>
                  <td className="mono">{formatDuration(run.durationMs)}</td>
                  <td>
                    <span className={statusChipClass(run.status)}>
                      <span className="dot" />
                      {run.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function statusChipClass(status: RecentRun['status']): string {
  switch (status) {
    case 'RUNNING':
      return 'chip info';
    case 'AWAITING SIG':
    case 'MONITOR':
      return 'chip warn';
    case 'FAILED':
      return 'chip fail';
    case 'SEALED':
    default:
      return 'chip';
  }
}

function formatDuration(ms: number): string {
  if (ms <= 0) return '—';
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  if (minutes < 60) return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes === 0 ? `${hours}h` : `${hours}h ${remMinutes}m`;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Please retry.';
}
